'use client';

import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useDragTime } from '@hooks/useDragTime';
import { useClockSettingsStore } from '@/store/timeStore';

/**
 * Provides the rendered clock-face width in CSS pixels so descendants
 * (ClusterMarker, in particular) can scale pill width / font size with
 * the actual dial size instead of fixed pixel constants. Default 400 is
 * a sensible mid-range fallback for the first render before the
 * ResizeObserver fires.
 */
export const ClockSizeContext = React.createContext<number>(400);

/**
 * A transparent PNG (data URL) of the dial's NON-gradient content — the number
 * labels and crosshairs — captured from `#clock-container` via html2canvas.
 * ClusterMarker composites it on top of its CSS gradient reconstruction so the
 * liquid-glass pills refract the labels/crosshairs too (the CSS reconstruction
 * alone can't reproduce text). The conic gradient is intentionally NOT captured
 * (html2canvas can't render conic gradients) — the pills rebuild that in CSS.
 * Empty string until the capture completes; pills simply skip the layer.
 */
export const DialTextureContext = React.createContext<string>('');

interface ClockRingProps {
  children: React.ReactNode;
}

export function ClockRing({ children }: ClockRingProps) {
  const { isDragging, eventHandlers } = useDragTime();
  const ref = useRef<HTMLDivElement>(null);
  const [clockPx, setClockPx] = useState<number>(400);
  const [dialTexture, setDialTexture] = useState<string>('');
  // Recapture when the label TEXT changes (12h/24h) or the theme flips (label +
  // crosshair colours differ light/dark).
  const hour12 = useClockSettingsStore((s) => s.timeFormat.hour12);
  const isDark = useSyncExternalStore(
    (onStoreChange) => {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener('change', onStoreChange);
      return () => mq.removeEventListener('change', onStoreChange);
    },
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
    () => false,
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = (w: number) => setClockPx(Math.round(w));
    update(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr) update(cr.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Capture the labels + crosshairs to a transparent texture. Static content —
  // runs only on size / format / theme change, never per clock tick.
  useEffect(() => {
    if (clockPx <= 0) return;
    let cancelled = false;
    const capture = async () => {
      const container = document.getElementById('clock-container');
      if (!container) return;
      try {
        const { default: html2canvas } = await import('html2canvas');
        const canvas = await html2canvas(container, {
          backgroundColor: null, // transparent — keep only labels + crosshairs
          scale: window.devicePixelRatio || 1,
          logging: false,
          // Exclude the dial face + conic gradient; keep #clock-number-labels
          // and #clock-crosshairs.
          ignoreElements: (el) =>
            el.id === 'clock-gradient' || el.id === 'clock-dial-face',
        });
        if (!cancelled) setDialTexture(canvas.toDataURL());
      } catch {
        // Capture failed (unsupported / blocked) — pills just skip the label
        // layer and still refract the gradient. No hard failure.
      }
    };
    // Let layout + web fonts settle so the labels render at final size.
    const t = setTimeout(capture, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [clockPx, hour12, isDark]);

  const sizeValue = useMemo(() => clockPx, [clockPx]);

  return (
    <div
      ref={ref}
      className="absolute inset-[0] cursor-grab clock-ring-wrapper"
      onMouseDown={eventHandlers.onMouseDown}
      onTouchStart={eventHandlers.onTouchStart}
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
      }}
    >
      <ClockSizeContext.Provider value={sizeValue}>
        <DialTextureContext.Provider value={dialTexture}>
          {children}
        </DialTextureContext.Provider>
      </ClockSizeContext.Provider>
    </div>
  );
}
